/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import 'chartjs-adapter-date-fns';
import React from 'react';
import { useAtomValue } from 'jotai';
import { getOutputStatisticsBySectionId } from './atoms';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  TimeScale,
} from 'chart.js';
import { Box, CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';
import { debouncedViewStateAtom } from '../map/atoms';
import { cameraBbox } from '../map/geometryDetail';
import { incidentFiltersSelector } from './ExposureStats';
import { IncidentCountyChart } from './IncidentCountyChart';
import { IncidentTimeSeriesChart } from './IncidentTimeSeriesChart';
import { ErrorBoundary } from '../components/ErrorBoundary';

ChartJS.register(
  TimeScale,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const IncidentChart = ({ context }: { context?: string }) => {
  const { incidentId, sectionId } = useParams();

  const { withinView: filterByView } = useAtomValue(incidentFiltersSelector(incidentId));
  const viewState = useAtomValue(debouncedViewStateAtom);
  const selectedDataSet =
    getOutputStatisticsBySectionId(sectionId ?? 'population')[0]?.outStatisticFieldName ?? null;

  // The map canvas's bounds, from the dimensions the camera publishes -- an
  // unsized viewport would answer for a 1x1 pixel canvas. Null until the map has
  // measured its canvas.
  const cameraBounds = React.useMemo(() => {
    const bbox = cameraBbox(viewState);
    if (bbox == null) {
      return null;
    }
    return { xmin: bbox[0], ymin: bbox[1], xmax: bbox[2], ymax: bbox[3] };
  }, [viewState]);

  const viewStateEnvelope = filterByView ? (cameraBounds ?? undefined) : undefined;

  // Same reason the stats tiles hold still (see ExposureStats): with the view
  // filter on and no envelope to apply, the charts would be drawn from national
  // aggregations while the toggle reads as filtered. Neither chart is mounted, so
  // neither queries, until the map publishes its canvas size.
  const viewFilterPending = filterByView && cameraBounds == null;

  const chartSubject = `${incidentId ?? ''}|${sectionId ?? ''}|${selectedDataSet ?? ''}`;

  return (
    <React.Suspense fallback={null}>
      <Box
        sx={{
          backgroundColor: context === 'wowcast' ? 'rgba(0, 62, 103, 0.2)' : 'white',
          backdropFilter: 'blur(10px)',
          borderRadius: '4px',
          p: 2,
          position: 'relative',
          minHeight: 400,
          height: '100%',
          width: '100%',

          // '@media print': {
          //   width: '1020px',
          // },
        }}
      >
        {viewFilterPending ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 400,
              height: '100%',
            }}
          >
            <CircularProgress color="primary" aria-label="Waiting for the map view" />
          </Box>
        ) : (
          selectedDataSet != null && (
            <React.Suspense fallback={null}>
              {/* The two charts query different aggregations and are wrapped
                  separately so one that cannot be drawn -- or whose query
                  rejects -- leaves the other one readable. The reset subject
                  deliberately excludes the camera envelope: panning must not
                  re-run a query that has already failed. */}
              <ErrorBoundary region="The county chart" resetKey={chartSubject}>
                <IncidentCountyChart
                  context={context}
                  yAttribute={selectedDataSet}
                  bounds={viewStateEnvelope}
                />
              </ErrorBoundary>
              <ErrorBoundary region="The time series chart" resetKey={chartSubject}>
                <IncidentTimeSeriesChart
                  context={context}
                  yAttribute={selectedDataSet}
                  bounds={viewStateEnvelope}
                />
              </ErrorBoundary>
            </React.Suspense>
          )
        )}
      </Box>
    </React.Suspense>
  );
};
